import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Activity,
  ActivityType,
  AuditEntry,
  Attachment,
  CreateActivityInput,
  CreateLeadInput,
  CustomField,
  Lead,
  LeadGroup,
  Member,
  PipelineReport,
  SavedView,
  Stage,
  Tag,
  UpdateLeadInput,
} from '@figureit/shared';
import { api, apiUrl } from './client';

// ---- Leads ----
export interface ListLeadsParams {
  search?: string;
  stageId?: string;
  priority?: 'A' | 'B' | 'C';
  ownerUserId?: string;
  tagIds?: string[];
  overdueOnly?: boolean;
  staleOnly?: boolean;
  includeDeleted?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

const listLeadsQuery = (p: ListLeadsParams) => {
  const qs = new URLSearchParams();
  if (p.search) qs.set('search', p.search);
  if (p.stageId) qs.set('stageId', p.stageId);
  if (p.priority) qs.set('priority', p.priority);
  if (p.ownerUserId) qs.set('ownerUserId', p.ownerUserId);
  if (p.tagIds?.length) qs.set('tagIds', p.tagIds.join(','));
  if (p.overdueOnly) qs.set('overdueOnly', 'true');
  if (p.staleOnly) qs.set('staleOnly', 'true');
  if (p.includeDeleted) qs.set('includeDeleted', 'true');
  if (p.sortBy) qs.set('sortBy', p.sortBy);
  if (p.sortDir) qs.set('sortDir', p.sortDir);
  return qs.toString();
};

export const useLeads = (params: ListLeadsParams = {}) =>
  useQuery({
    queryKey: ['leads', params],
    queryFn: () => api<{ items: Lead[] }>(`/api/leads?${listLeadsQuery(params)}`),
  });

export const useCreateLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeadInput) =>
      api<Lead>('/api/leads', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
};

export const useUpdateLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateLeadInput) =>
      api<Lead>(`/api/leads/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
      qc.invalidateQueries({ queryKey: ['report'] });
    },
  });
};

export const useDeleteLead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/leads/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
};

export const useReorderLeads = () => {
  const qc = useQueryClient();
  return useMutation({
    // `groupId` (when the key is present) re-assigns the reordered leads to that
    // group — null moves them to "Ungrouped". Omit it to only reposition.
    mutationFn: (input: string[] | { ids: string[]; groupId?: string | null }) => {
      const body = Array.isArray(input) ? { ids: input } : input;
      return api('/api/leads/reorder', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
};

export const useBulkLeadAction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      ids: string[];
      action: 'set-stage' | 'set-owner' | 'set-priority' | 'set-group' | 'add-tags' | 'delete';
      stageId?: string;
      ownerUserId?: string | null;
      priority?: 'A' | 'B' | 'C';
      groupId?: string | null;
      tagIds?: string[];
    }) => api('/api/leads/bulk', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
};

// ---- Activities ----
export const useActivities = (leadId: string | null | undefined) =>
  useQuery({
    queryKey: ['activities', leadId],
    queryFn: () => api<Activity[]>(`/api/leads/${leadId}/activities`),
    enabled: !!leadId,
  });

export const useAddActivity = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateActivityInput) =>
      api<Activity>(`/api/leads/${leadId}/activities`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead-audit', leadId] });
    },
  });
};

export const useDeleteActivity = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activityId: string) =>
      api(`/api/activities/${activityId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activities', leadId] }),
  });
};

// ---- Stages ----
export const useStages = () =>
  useQuery({
    queryKey: ['stages'],
    queryFn: () => api<{ items: Stage[] }>('/api/stages'),
  });

export const useStageMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['stages'] });
  return {
    create: useMutation({
      mutationFn: (s: Partial<Stage>) =>
        api<Stage>('/api/stages', { method: 'POST', body: JSON.stringify(s) }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...s }: Partial<Stage> & { id: string }) =>
        api<Stage>(`/api/stages/${id}`, { method: 'PATCH', body: JSON.stringify(s) }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/stages/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) =>
        api('/api/stages/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
      onSuccess: invalidate,
    }),
  };
};

// ---- Lead groups ----
export const useLeadGroups = () =>
  useQuery({
    queryKey: ['lead-groups'],
    queryFn: () => api<{ items: LeadGroup[] }>('/api/groups'),
  });

export const useLeadGroupMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['lead-groups'] });
  return {
    create: useMutation({
      mutationFn: (input: { name: string; color?: string }) =>
        api<LeadGroup>('/api/groups', { method: 'POST', body: JSON.stringify(input) }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: { id: string; name?: string; color?: string }) =>
        api<LeadGroup>(`/api/groups/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/groups/${id}`, { method: 'DELETE' }),
      // A deleted group detaches its leads (they become Ungrouped) — refresh both.
      onSuccess: () => {
        invalidate();
        qc.invalidateQueries({ queryKey: ['leads'] });
      },
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) =>
        api('/api/groups/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
      onSuccess: invalidate,
    }),
  };
};

// ---- Activity types ----
export const useActivityTypes = () =>
  useQuery({
    queryKey: ['activity-types'],
    queryFn: () => api<{ items: ActivityType[] }>('/api/activity-types'),
  });

export const useActivityTypeMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['activity-types'] });
  return {
    create: useMutation({
      mutationFn: (name: string) =>
        api('/api/activity-types', { method: 'POST', body: JSON.stringify({ name }) }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (input: { id: string; name?: string; template?: string | null }) => {
        const { id, ...body } = input;
        return api(`/api/activity-types/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      },
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/activity-types/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
};

// ---- Custom fields ----
export const useCustomFields = () =>
  useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => api<{ items: CustomField[] }>('/api/custom-fields'),
  });

export const useCustomFieldMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['custom-fields'] });
  return {
    create: useMutation({
      mutationFn: (cf: Partial<CustomField>) =>
        api('/api/custom-fields', { method: 'POST', body: JSON.stringify(cf) }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...cf }: Partial<CustomField> & { id: string }) =>
        api(`/api/custom-fields/${id}`, { method: 'PATCH', body: JSON.stringify(cf) }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/custom-fields/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
};

// ---- Tags ----
export const useTags = () =>
  useQuery({
    queryKey: ['tags'],
    queryFn: () => api<{ items: Tag[] }>('/api/tags'),
  });

export const useTagMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['tags'] });
  return {
    create: useMutation({
      mutationFn: (input: { name: string; color?: string }) =>
        api<Tag>('/api/tags', { method: 'POST', body: JSON.stringify(input) }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: { id: string; name?: string; color?: string }) =>
        api<Tag>(`/api/tags/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/tags/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
};

// ---- Saved views ----
export const useSavedViews = () =>
  useQuery({
    queryKey: ['saved-views'],
    queryFn: () => api<{ items: SavedView[] }>('/api/saved-views'),
  });

export const useSavedViewMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['saved-views'] });
  return {
    create: useMutation({
      mutationFn: (v: Omit<SavedView, 'id'>) =>
        api<SavedView>('/api/saved-views', {
          method: 'POST',
          body: JSON.stringify(v),
        }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...v }: Partial<SavedView> & { id: string }) =>
        api<SavedView>(`/api/saved-views/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(v),
        }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/saved-views/${id}`, { method: 'DELETE' }),
      onSuccess: invalidate,
    }),
  };
};

// ---- Org / members / invites ----
export const useMembers = () =>
  useQuery({
    queryKey: ['members'],
    queryFn: () => api<{ items: Member[] }>('/api/org/members'),
  });

export const useInvites = () =>
  useQuery({
    queryKey: ['invites'],
    queryFn: () => api<{ items: { id: string; email: string; role: string; expiresAt: string }[] }>('/api/org/invites'),
  });

// ---- Reports ----
export const useReports = () =>
  useQuery({
    queryKey: ['report', 'pipeline'],
    queryFn: () => api<PipelineReport>('/api/reports/pipeline'),
  });

// ---- Audit ----
export const useLeadAudit = (leadId: string | null | undefined) =>
  useQuery({
    queryKey: ['lead-audit', leadId],
    queryFn: () => api<{ items: AuditEntry[] }>(`/api/leads/${leadId}/audit`),
    enabled: !!leadId,
  });

// ---- Attachments ----
export const useAttachments = (leadId: string | null | undefined) =>
  useQuery({
    queryKey: ['attachments', leadId],
    queryFn: () => api<{ items: Attachment[] }>(`/api/leads/${leadId}/attachments`),
    enabled: !!leadId,
  });

export const uploadAttachment = async (leadId: string, file: File): Promise<Attachment> => {
  const fd = new FormData();
  fd.append('file', file);
  return api<Attachment>(`/api/leads/${leadId}/attachments`, { method: 'POST', body: fd });
};

// ---- Import / export ----
export const exportCsvUrl = () => apiUrl('/api/export/leads.csv');
export const exportBackupUrl = () => apiUrl('/api/export/backup');

export const importLeadsCsv = (csv: string) =>
  api<{ created: number; failures: { row: number; reason: string }[] }>(
    '/api/import/leads',
    { method: 'POST', body: JSON.stringify({ csv }) },
  );

export interface ImportSummary {
  stagesCreated: string[];
  activityTypesCreated: string[];
  customFieldsCreated: string[];
  customFieldOptionsExtended: { label: string; added: string[] }[];
  ownersCreated: { name: string; email: string }[];
  leadsInserted: number;
  leadsUpdated: number;
  activitiesInserted: number;
  activitiesSkipped: number;
  totals: {
    leads: number;
    activities: number;
    stages: number;
    activityTypes: number;
    customFields: number;
  };
}

export const importBackup = (data: unknown) =>
  api<{ ok: true; summary: ImportSummary }>('/api/import/backup', {
    method: 'POST',
    body: JSON.stringify({ data }),
  });
